import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  UserProfile, 
  UserLogin, 
  UserRegistration,
  authService,
  usersService,
  apiClient,
  isAuthenticated as checkIsAuthenticated,
  ensureAuthenticated,
  ApiError
} from '../services';

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: UserLogin) => Promise<void>;
  register: (userData: UserRegistration) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is authenticated and token is valid
        if (checkIsAuthenticated()) {
          // Ensure token is still valid and refresh if needed
          const tokenValid = await ensureAuthenticated();
          if (tokenValid) {
            // Fetch current user profile
            const userProfile = await usersService.getCurrentUser();
            setUser(userProfile);
          } else {
            // Token refresh failed, user needs to login again
            apiClient.clearTokens();
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        apiClient.clearTokens();
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    let refreshInterval: number;

    if (user && checkIsAuthenticated()) {
      // Refresh token every 14 minutes (assuming 15min expiry)
      refreshInterval = window.setInterval(async () => {
        try {
          await ensureAuthenticated();
        } catch (error) {
          console.error('Token refresh failed:', error);
          await handleLogout();
        }
      }, 14 * 60 * 1000); // 14 minutes
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, [user]);

  const handleLogin = async (credentials: UserLogin): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await authService.login(credentials);
      const userProfile = await usersService.getCurrentUser();
      setUser(userProfile);
    } catch (error) {
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Login failed. Please try again.');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (userData: UserRegistration): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await authService.register(userData);
      const userProfile = await usersService.getCurrentUser();
      setUser(userProfile);
    } catch (error) {
      if (error instanceof ApiError) {
        // Handle validation errors from the API
        if (error.status === 422 && error.data?.detail) {
          if (Array.isArray(error.data.detail)) {
            // Format validation errors
            const errorMessages = error.data.detail.map((err: any) => 
              `${err.loc?.join(' > ')}: ${err.msg}`
            ).join(', ');
            setError(errorMessages);
          } else {
            setError(error.data.detail);
          }
        } else {
          setError(error.message);
        }
      } else {
        setError('Registration failed. Please try again.');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  };

  const refreshUser = async (): Promise<void> => {
    if (!checkIsAuthenticated()) return;
    
    try {
      const userProfile = await usersService.getCurrentUser();
      setUser(userProfile);
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      if (error instanceof ApiError && error.status === 401) {
        // Token is invalid, logout user
        await handleLogout();
      }
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: checkIsAuthenticated() && user !== null,
    isLoading,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    refreshUser,
    clearError,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;